// src/features/resources/components/ResourceModal.js
import React, { useEffect, useState } from "react";
import {
  Box,
  Modal,
  Typography,
  TextField,
  Button,
  IconButton,
  MenuItem,
  Divider,
  CircularProgress,
  Alert
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useAuth } from "../../contexts/AuthContext";
import FolderSelector from "./FolderSelector";
import {
  uploadResourceFile,
  createResource,
  updateResource,
  fetchResourceById
} from "../../services/resourceService";
import { fetchFolderById } from "../../services/folderService";

// styles like CreatePostModal
const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%,-50%)",
  width: { xs: "92%", sm: 640 },
  maxHeight: "90vh",
  bgcolor: "background.paper",
  borderRadius: 2,
  p: 3,
  overflow: "auto"
};

const GRADES = [
  "R","1","2","3","4","5","6","7","8","9","10","11","12"
];

const ResourceModal = ({ open, onClose, onSaved, editResourceId }) => {
  const { currentUser, userProfile } = useAuth();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [folderId, setFolderId] = useState("");
  const [type, setType] = useState(""); // pdf, video, audio, youtube, link
  const [file, setFile] = useState(null);
  const [url, setUrl] = useState("");
  const [subjectId, setSubjectId] = useState("");
  const [grade, setGrade] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");

  // load existing resource for edit
  useEffect(() => {
    const load = async () => {
      if (editResourceId) {
        const r = await fetchResourceById(editResourceId);
        if (r) {
          setTitle(r.title || "");
          setDescription(r.description || "");
          setFolderId(r.folderId || "");
          setType(r.type || "");
          setUrl(r.fileUrl || "");
          setSubjectId(r.subjectId || "");
          setGrade(r.grade || "");
        }
      } else {
        // reset
        setTitle("");
        setDescription("");
        setFolderId("");
        setType("");
        setFile(null);
        setUrl("");
        setSubjectId("");
        setGrade("");
      }
    };
    if (open) load();
  }, [open, editResourceId]);

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
      // auto set type if not set
      if (!type) {
        const mime = e.target.files[0].type;
        if (mime === "application/pdf") setType("pdf");
        else if (mime.startsWith("video")) setType("video");
        else if (mime.startsWith("audio")) setType("audio");
      }
    }
  };

  const handleSubmit = async () => {
    setError("");
    if (!currentUser) {
      setError("Please sign in to upload resources.");
      return;
    }
    if (!title.trim()) {
      setError("Title is required.");
      return;
    }
    if (!folderId) {
      setError("Select a folder.");
      return;
    }
    if (!type) {
      setError("Select the resource type.");
      return;
    }

    setIsSubmitting(true);
    try {
      // inherit folder metadata
      const folder = await fetchFolderById(folderId);

      let fileData = {};
      if (file) {
        fileData = await uploadResourceFile(currentUser.uid, file);
      }

      const payload = {
        userId: currentUser.uid,
        title: title.trim(),
        description: description.trim() || "",
        folderId,
        type,
        subjectId: subjectId || "",
        grade: grade || "",
        fileUrl: fileData.url || url || "",
        filePath: fileData.path || "",
        visibility: folder?.visibility || "private",
        institutionId: folder?.institutionId || userProfile?.institutionId || null
      };

      if (editResourceId) {
        await updateResource(editResourceId, payload);
      } else {
        await createResource(payload);
      }

      onSaved && onSaved();
      onClose && onClose();
    } catch (err) {
      console.error("Failed saving resource:", err);
      setError("Failed to save resource. Try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={style}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" fontWeight="bold">
            {editResourceId ? "Edit Resource" : "Add Resource"}
          </Typography>
          <IconButton size="small" onClick={onClose}><CloseIcon /></IconButton>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <TextField
          fullWidth
          label="Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          sx={{ mb: 2 }}
        />

        <FolderSelector value={folderId} onChange={setFolderId} allowCreate />

        <TextField
          select
          label="Type"
          fullWidth
          value={type}
          onChange={(e) => setType(e.target.value)}
          sx={{ mb: 2 }}
        >
          <MenuItem value="pdf">PDF</MenuItem>
          <MenuItem value="video">Video (upload)</MenuItem>
          <MenuItem value="audio">Audio</MenuItem>
          <MenuItem value="youtube">YouTube Link</MenuItem>
          <MenuItem value="link">Web Link</MenuItem>
        </TextField>

        {["pdf", "video", "audio"].includes(type) && (
          <Box sx={{ mb: 2 }}>
            <input type="file" onChange={handleFileChange} />
          </Box>
        )}

        {["youtube", "link"].includes(type) && (
          <TextField
            fullWidth
            label={type === "youtube" ? "YouTube URL" : "URL"}
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            sx={{ mb: 2 }}
          />
        )}

        <TextField
          fullWidth
          label="Subject (optional)"
          placeholder="Subject id or leave blank"
          value={subjectId}
          onChange={(e) => setSubjectId(e.target.value)}
          sx={{ mb: 2 }}
        />

        <TextField
          select
          label="Grade (optional)"
          fullWidth
          value={grade}
          onChange={(e) => setGrade(e.target.value)}
          sx={{ mb: 2 }}
        >
          <MenuItem value="">— Select grade —</MenuItem>
          {GRADES.map(g => <MenuItem key={g} value={g}>{g}</MenuItem>)}
        </TextField>

        <TextField
          fullWidth
          label="Description (optional)"
          multiline
          rows={3}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          sx={{ mb: 2 }}
        />

        <Box display="flex" justifyContent="flex-end" gap={2}>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? <><CircularProgress size={16} sx={{ mr: 1 }} />Saving...</> : (editResourceId ? "Save" : "Upload")}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default ResourceModal;
